// src/utils/timesheets.js
import { DateTime } from "luxon";
import { db } from "../firebase/config";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

/** Get ISO week key in company TZ (e.g. "2025-41") */
export function isoWeekKey(isoStringOrDate, tz) {
  const dt =
    typeof isoStringOrDate === "string"
      ? DateTime.fromISO(isoStringOrDate, { zone: tz })
      : DateTime.fromJSDate(isoStringOrDate, { zone: tz });

  return dt.toFormat("kkkk-ww"); // ISO week-numbering year + week
}

/** Given a week key + tz, return start (Mon 00:00) & end (Sun 23:59:59.999) ISO */
export function weekBoundsFromKey(weekKey, tz = "UTC") {
  // Expect "YYYY-WW" (e.g., "2025-41")
  const m = /^(\d{4})-(\d{2})$/.exec(weekKey || "");
  if (!m) {
    // fallback to "this week" if key is malformed
    const now = DateTime.now().setZone(tz);
    const start = now.startOf("week"); // Monday if you configured locale; else Sunday
    const end = start.plus({ days: 6 }).endOf("day");
    return { startISO: start.toISO(), endISO: end.toISO() };
  }

  const year = Number(m[1]);
  const week = Number(m[2]);

  const start = DateTime.fromObject(
    { weekYear: year, weekNumber: week, weekday: 1 },
    { zone: tz }
  ).startOf("day");

  const end = start.plus({ days: 6 }).endOf("day");

  return { startISO: start.toISO(), endISO: end.toISO() };
}

/** Firestore doc id for a timesheet (userId + week) */
export function timesheetIdFor(userId, weekKey) {
  return `${userId}_${weekKey}`;
}

/** Check if the entry’s week is approved/locked */
export async function isEntryLocked(companyId, entry, tz) {
  if (!entry?.userId) return false;
  const wk = isoWeekKey(entry.clockIn || entry.date, tz);
  const ref = doc(
    db,
    `companies/${companyId}/timesheets`,
    timesheetIdFor(entry.userId, wk)
  );
  const snap = await getDoc(ref);
  if (!snap.exists()) return false;
  const status = snap.data()?.status;
  return status === "approved" || status === "locked";
}

/** Approve a timesheet (creates/updates doc) */
export async function approveTimesheet({
  companyId,
  userId,
  weekKey,
  timezone,
  approver,
}) {
  const ref = doc(
    db,
    `companies/${companyId}/timesheets`,
    timesheetIdFor(userId, weekKey)
  );
  const { startISO, endISO } = weekBoundsFromKey(weekKey, timezone);
  await setDoc(
    ref,
    {
      userId,
      timezone,
      periodStartISO: startISO,
      periodEndISO: endISO,
      status: "approved",
      approvedAt: serverTimestamp(),
      approvedBy: approver, // {uid, name, role}
    },
    { merge: true }
  );
}

/** Unlock (back to submitted) */
export async function unlockTimesheet({ companyId, userId, weekKey }) {
  const ref = doc(
    db,
    `companies/${companyId}/timesheets`,
    timesheetIdFor(userId, weekKey)
  );
  await updateDoc(ref, { status: "submitted" });
}
